<template>
    <ElevatedCard :widthType="'twoThirds'">
        <HeaderWidget :iconUrl="loadIcon" :title="widgetTitle" @click="onPressHeader" />
        <div v-if="widget" class="regles-widget-group">
            <ReglePastille cle="dashboard.widgetPerimetreCommun" />
            <ReglePastille cle="dashboard.widgetPreventifFenetres" />
        </div>
        <div v-if="widget">
            <span v-if="widget.sites == null || widget.sites.length > 1" class="site-widget">{{
                    $t('multisites')
                }}</span>
            <span v-for="site in widget.sites" v-else :key="site.id" class="site-widget">
                {{ site.description }}
                <span v-if="site.id !== widget.sites[widget.sites.length - 1].id">, </span>
            </span>
        </div>
        <NoData
            v-if="!isWidgetLoading && isPreventiveCircleNoData && isPreventiveChartNoData"
            textNoData="widget.preventive.noData"
        />

        <div v-else class="graphs-container">
            <PieChart
                v-if="(!isPreventiveCircleNoData || isCircleLoading) && isEnded"
                :colors="[getColorEnCours(), getColorARealiser(), getColorRealiser()]"
                :height="'95%'"
                :isLoading="isCircleLoading || !isEnded"
                :labels="[
                    $t('widgetPreventive.inProgress') + ' (' + getPreventiveCircleData.inProgressTotal + ')',
                    $t('widgetPreventive.toDo') + ' (' + getPreventiveCircleData.awaitingRealizationTotal + ')',
                    $t('widgetPreventive.done') + ' (' + getPreventiveCircleData.achievedTotal + ')',
                ]"
                :seriesValues="donutSeries"
                :showDataLabels="true"
                :showLegend="true"
                :title="titlePie"
                :width="'350px'"
                class="pie-chart-container chart-width"
                position="right"
                @dataPointClicked="naviguerVersRecherchePie"
            />
            <BarGraph
                :categories="getMonths()"
                :colors="[getColorEnCours(), getColorARealiser(), getColorRealiser()]"
                :equipementInterventionNonPreventiveTooltip="equipementInterventionNonPreventiveTooltip"
                :loading="isChartLoading || isCircleLoading"
                :seriesData="barSeries"
                :showLegend="false"
                class="bar-graph-container chart-width2"
                @dataPointClicked="naviguerVersRechercheBar"
            />
        </div>
    </ElevatedCard>
</template>

<script lang="ts">
import dayjs from 'dayjs';
import localeFr from 'dayjs/locale/fr';
import ElevatedCard from '../../../components/ui-components/ElevatedCard.vue';
import HeaderWidget from '../../../components/ui-components/HeaderWidget.vue';
import { defineComponent, PropType } from 'vue';
import NoData from '@/components/ui-components/NoDataWidget.vue';
import { SuiviInterventionPreventiveActions } from '@/modules/SuiviInterventionsPreventives/store/action-types';
import { EtatsTravaux } from '@/models/InterventionNonPreventive';
import { store } from '@/plugins/store';
import { Widget } from '@/models/Widget';
import { FiltreStats } from '@/models/FiltreStats';
import { Site } from '@/models/NavFilter';
import { SUIVI_INTERVENTION_PREVENTIVE } from '@/modules/SuiviInterventionsPreventives/store/mutation-types';
import { PreventiveChartWidgetItems, PreventiveCircleWidgetItems } from '@/models/PreventiveWidgetItems';
import { EquipementAction } from '@/modules/Equipements/store/actions-types';
import PieChart from '@/components/ui-components/PieChart.vue';
import BarGraph from '@/components/ui-components/BarGraph.vue';
import HomeService from '../services/HomeService';
import ReglePastille from '@/components/regles/ReglePastille.vue';

dayjs.locale(localeFr);

export default defineComponent({
    name: 'Preventive',
    components: { PieChart, BarGraph, ElevatedCard, HeaderWidget, NoData, ReglePastille },
    props: {
        widget: { type: Object as PropType<Widget>, required: false },
        equipementInterventionNonPreventiveDataCircle: {
            type: Object as PropType<PreventiveCircleWidgetItems>,
            required: false,
        },
        equipementInterventionNonPreventiveDataChart: {
            type: Object as PropType<PreventiveChartWidgetItems>,
            required: false,
        },
        equipementInterventionNonPreventiveLoading: { type: Boolean, default: false },
        isBim: { type: Boolean, default: false },
        title: { type: String, required: false },
    },
    data() {
        const today = dayjs();
        const dateDebut = dayjs()
            .set('month', today.get('month') - 5)
            .startOf('month');

        const dateFin = dayjs()
            .set('month', today.get('month') + 6)
            .endOf('month');
        const dateFinMois = dayjs().endOf('month');
        const dateDebutCircleData = dateFinMois.set('month', dateFinMois.get('month') - 11).startOf('month');
        return {
            dateDebut,
            dateFin,
            dateFinMois,
            dateDebutCircleData,
            localCircleData: {
                inProgressTotal: 0,
                awaitingRealizationTotal: 0,
                achievedTotal: 0,
            },
            isCircleLoadingLocal: false,
            isChartLoadingLocal: false,
            barSeries: [
                {
                    name: 'En cours',
                    data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
                },
                {
                    name: 'A réaliser',
                    data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
                },
                {
                    name: 'Réalisées',
                    data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
                },
            ],
            donutSeries: [0, 0, 0],
            isEnded: false,
        };
    },
    watch: {
        equipementInterventionNonPreventiveDataCircle: {
            handler(newData) {
                if (this.isBim && newData) {
                    this.donutSeries = [
                        newData.inProgressTotal || 0,
                        newData.awaitingRealizationTotal || 0,
                        newData.achievedTotal || 0,
                    ];
                    this.checkIsEnded();
                }
            },
            immediate: true,
        },
        equipementInterventionNonPreventiveDataChart: {
            handler(newData) {
                if (this.isBim && newData) {
                    this.barSeries[0].data = newData.inProgress || [];
                    this.barSeries[1].data = newData.awaitingRealization || [];
                    this.barSeries[2].data = newData.achieved || [];
                    this.checkIsEnded();
                }
            },
            immediate: true,
        },
    },
    async beforeMount() {
        if (
            this.isBim &&
            this.equipementInterventionNonPreventiveDataCircle &&
            this.equipementInterventionNonPreventiveDataChart
        ) {
            this.donutSeries = [
                this.equipementInterventionNonPreventiveDataCircle.inProgressTotal,
                this.equipementInterventionNonPreventiveDataCircle.awaitingRealizationTotal,
                this.equipementInterventionNonPreventiveDataCircle.achievedTotal,
            ];
            this.barSeries[0].data = this.equipementInterventionNonPreventiveDataChart.inProgress;
            this.barSeries[1].data = this.equipementInterventionNonPreventiveDataChart.awaitingRealization;
            this.barSeries[2].data = this.equipementInterventionNonPreventiveDataChart.achieved;
            this.$nextTick(() => {
                this.isEnded = true;
            });
            return;
        }
        const siteIds: number[] = this.widget?.sites ? this.widget?.sites.map(site => site.id) : [];
        const filtre: FiltreStats = { siteIds: siteIds };
        this.isCircleLoadingLocal = true;
        this.isChartLoadingLocal = true;
        const [circleResult, chartResult] = await Promise.allSettled([
            HomeService.getPreventiveCircleData({
                dateDebut: this.dateDebutCircleData.format('YYYY-MM-DD'),
                dateFin: this.dateFinMois.format('YYYY-MM-DD'),
                filtre: filtre,
            }),
            HomeService.getPreventiveChartData({
                dateDebut: this.dateDebut.format('YYYY-MM-DD'),
                dateFin: this.dateFin.format('YYYY-MM-DD'),
                filtre: filtre,
            }),
        ]);
        this.isCircleLoadingLocal = false;
        this.isChartLoadingLocal = false;
        const circleResponse = circleResult.status === 'fulfilled' ? circleResult.value : undefined;
        const chartResponse = chartResult.status === 'fulfilled' ? chartResult.value : undefined;
        if (circleResult.status === 'rejected' || chartResult.status === 'rejected') {
            console.error('[Widget Préventif] erreur de chargement', circleResult, chartResult);
        }

        if (circleResponse?.data) {
            this.localCircleData = {
                inProgressTotal: circleResponse.data.nbEnCours || 0,
                awaitingRealizationTotal: circleResponse.data.nbARealiser || 0,
                achievedTotal: circleResponse.data.nbRealisees || 0,
            };
        }
        this.donutSeries = [
            this.localCircleData.inProgressTotal,
            this.localCircleData.awaitingRealizationTotal,
            this.localCircleData.achievedTotal,
        ];
        if (chartResponse?.data) {
            const enCours: number[] = [];
            const realisees: number[] = [];
            const aRealiser: number[] = [];
            chartResponse.data.forEach((element: any) => {
                enCours.push(element.nbEnCours);
                realisees.push(element.nbRealisees);
                aRealiser.push(element.nbARealiser);
            });
            this.barSeries[0].data = enCours;
            this.barSeries[1].data = aRealiser;
            this.barSeries[2].data = realisees;
        }
        this.$nextTick(() => {
            this.isEnded = true;
        });
    },
    methods: {
        getColorEnCours() {
            return getComputedStyle(document.documentElement).getPropertyValue('--en-cours');
        },
        getColorARealiser() {
            return getComputedStyle(document.documentElement).getPropertyValue('--a-realiser');
        },
        getColorRealiser() {
            return getComputedStyle(document.documentElement).getPropertyValue('--fait');
        },
        onChartClick(event: any, config: any, chartContext: any) {
            const index = chartContext.dataPointIndex;
            if (index !== -1) {
                this.naviguerVersRecherchePie(index);
            }
        },
        onPressHeader() {
            if (this.isBim) {
                this.$store.dispatch(`equipementState/${EquipementAction.INTERVENTIONS_PREVENTIVES_NAV_FILTER}`, []);
                return;
            }
            if (!store.getters.getUserInformations.fonctions.includes('consulter.interventionP')) {
                return;
            }
            // Dashboard personnalisé = widget avec des sites (le dashboard par défaut n'en a pas)
            const siteIds = (this.widget?.sites ?? []).map(site => site.id);
            if (siteIds.length > 0) {
                this.$router.push({ path: '/suivi-preventif', query: { siteIds: siteIds.join(',') } });
            } else {
                this.$router.push('/suivi-preventif');
            }
        },
        getMonths(): string[] {
            const start = dayjs().subtract(5, 'month');
            const months = [];
            const locale = this.$store.getters.getLangueSelectionnee?.locale || 'fr';
            for (let i = 0; i < 12; i++) {
                months.push(start.locale(locale).clone().add(i, 'month').format('MMM'));
            }
            return months;
        },
        getEtatFromIndex(index: number): EtatsTravaux | undefined {
            switch (index) {
                case 0:
                    return EtatsTravaux.enCours;
                case 1:
                    return EtatsTravaux.aRealiser;
                case 2:
                    return EtatsTravaux.realise;
                default:
                    break;
            }
        },
        naviguerVersRecherchePie(dataPointIndex: number) {
            if (this.isBim) {
                const etat = this.getEtatFromIndex(dataPointIndex);

                setTimeout(() => {
                    this.$store.dispatch(
                        `equipementState/${EquipementAction.INTERVENTIONS_PREVENTIVES_NAV_FILTER}`,
                        etat ? [etat] : []
                    );
                }, 0);

                return;
            }
            if (!store.getters.getUserInformations.fonctions.includes('consulter.interventionP')) {
                return;
            }
            const sites: Site[] = this.widget?.sites == null ? [] : this.widget?.sites.map(site => ({ id: site.id }));
            this.$store.commit(
                `suiviInterventionPreventiveModuleState/${SUIVI_INTERVENTION_PREVENTIVE.SET_FILTER_SITE}`,
                sites
            );

            this.$store.dispatch(
                `suiviInterventionPreventiveModuleState/${SuiviInterventionPreventiveActions.SET_FILTER}`,
                [this.getEtatFromIndex(dataPointIndex)]
            );
            this.$store.dispatch(
                `suiviInterventionPreventiveModuleState/${SuiviInterventionPreventiveActions.SET_DATE}`,
                {
                    dateDebutPeriodeRechercheParDate: this.dateDebutCircleData.format('YYYY-MM-DD'),
                    dateFinPeriodeRechercheParDate: this.dateFinMois.format('YYYY-MM-DD'),
                }
            );
            this.$router.push('/suivi-interventions-preventives');
        },
        naviguerVersRechercheBar(clickEvent: { seriesIndex: number; dataPointIndex: number }) {
            if (this.isBim) {
                const etat = this.getEtatFromIndex(clickEvent.seriesIndex);
                this.$store.dispatch(
                    `equipementState/${EquipementAction.INTERVENTIONS_PREVENTIVES_NAV_FILTER}`,
                    etat ? [etat] : []
                );
                return;
            }
            if (!store.getters.getUserInformations.fonctions.includes('consulter.interventionP')) {
                return;
            }
            //dataPointIndex est le mois cliqué sur le barGraph avec 0 = mois de this.dateDebut
            const dateDebutMoisRecherche = this.dateDebut.add(clickEvent.dataPointIndex, 'month');
            const dateFinMoisRecherche = dateDebutMoisRecherche.endOf('month');
            const sites: Site[] = this.widget?.sites == null ? [] : this.widget?.sites.map(site => ({ id: site.id }));
            this.$store.commit(
                `suiviInterventionPreventiveModuleState/${SUIVI_INTERVENTION_PREVENTIVE.SET_FILTER_SITE}`,
                sites
            );

            this.$store.dispatch(
                `suiviInterventionPreventiveModuleState/${SuiviInterventionPreventiveActions.SET_FILTER}`,
                [this.getEtatFromIndex(clickEvent.seriesIndex)]
            );
            this.$store.dispatch(
                `suiviInterventionPreventiveModuleState/${SuiviInterventionPreventiveActions.SET_DATE}`,
                {
                    dateDebutPeriodeRechercheParDate: dateDebutMoisRecherche.format('YYYY-MM-DD'),
                    dateFinPeriodeRechercheParDate: dateFinMoisRecherche.format('YYYY-MM-DD'),
                }
            );

            this.$router.push('/suivi-interventions-preventives');
        },
        checkIsEnded() {
            if (this.isBim) {
                if (
                    this.equipementInterventionNonPreventiveDataCircle &&
                    this.equipementInterventionNonPreventiveDataChart
                ) {
                    this.isEnded = true;
                }
            }
        },
    },
    computed: {
        isCircleLoading() {
            return this.widget ? this.isCircleLoadingLocal : this.equipementInterventionNonPreventiveLoading;
        },
        isChartLoading() {
            return this.widget ? this.isChartLoadingLocal : this.equipementInterventionNonPreventiveLoading;
        },
        isWidgetLoading() {
            return this.widget
                ? this.isChartLoadingLocal || this.isCircleLoadingLocal
                : this.equipementInterventionNonPreventiveLoading;
        },
        isError() {
            return false;
        },
        getPreventiveChartData() {
            return (
                this.equipementInterventionNonPreventiveDataChart || {
                    inProgress: this.barSeries[0].data,
                    awaitingRealization: this.barSeries[1].data,
                    achieved: this.barSeries[2].data,
                }
            );
        },
        getPreventiveCircleData() {
            return this.equipementInterventionNonPreventiveDataCircle || this.localCircleData;
        },
        isPreventiveCircleNoData() {
            const data = this.getPreventiveCircleData;
            return (data.inProgressTotal || 0) + (data.awaitingRealizationTotal || 0) + (data.achievedTotal || 0) === 0;
        },
        isPreventiveChartNoData() {
            const data = this.getPreventiveChartData;
            const sum = (arr: number[]) => arr?.reduce((a, b) => a + b, 0) || 0;
            return sum(data.inProgress) + sum(data.awaitingRealization) + sum(data.achieved) === 0;
        },
        loadIcon() {
            return require('@/assets/img/menu/calendar_month.png');
        },
        widgetTitle() {
            return this.title || (this.widget ? this.widget.libelle : '');
        },
        titlePie(): string {
            const locale = this.$store.getters.getLangueSelectionnee?.locale || 'fr';
            return (
                this.$t('widgetPreventive.from') +
                ' ' +
                this.dateDebutCircleData.locale(locale).format('MMMM YYYY') +
                ' ' +
                this.$t('widgetPreventive.to') +
                ' ' +
                this.dateFinMois.locale(locale).format('MMMM YYYY')
            );
        },
        equipementInterventionNonPreventiveTooltip() {
            return ({ series, dataPointIndex, w }: any) => {
                let currentTotal = 0;
                series.forEach((s: any) => {
                    currentTotal += s[dataPointIndex];
                });

                const value0 = w.globals.initialSeries[0].data[dataPointIndex];
                const label0 = w.globals.seriesNames[0];
                const value1 = w.globals.initialSeries[1].data[dataPointIndex];
                const label1 = w.globals.seriesNames[1];
                const value2 = w.globals.initialSeries[2].data[dataPointIndex];
                const label2 = w.globals.seriesNames[2];
                return `
                    <div class="equipementInterventionNonPreventive-tooltip">
                      <span class="tooltip-label" style='color: var(--en-cours)'><b>${label0} : </b></span>
                      <span class="tooltip-value">${value0}</span><br>
                      <span class="tooltip-label" style='color: var(--a-realiser)'><b>${label1} : </b></span>
                      <span class="tooltip-value">${value1}</span><br>
                      <span class="tooltip-label" style='color: var(--fait)'><b>${label2} : </b></span>
                      <span class="tooltip-value">${value2}</span><br>
                      <span><b>Total: </b>
                            ${currentTotal}
                        </span>
                    </div>
                  `;
            };
        },
    },
});
</script>

<style scoped>
.regles-widget-group {
    display: flex;
    align-items: center;
    gap: 16px;
}
.graphs-container {
    display: flex;
    flex-direction: column;
    padding: 10px 20px;
    height: 75%;
}
@media screen and (max-height: 800px) {
    .graphs-container {
        height: 65%;
    }
}
@media screen and (min-width: 980px) {
    .graphs-container {
        flex-direction: row;
    }
}
@media screen and (min-height: 800px) {
    .graphs-container {
        height: 76%;
    }
}
.chart-width {
    width: 35%;
}
.chart-width2 {
    width: 100%;
}

.site-widget {
    margin-left: 1.5%;
}
.site-widget-item {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-left: 1.5%;
}
.site-contract {
    font-size: 11px;
    color: var(--dark-grey);
}
</style>
